/*
 * Opted out of the React Compiler, and it has to be every form in the panel
 * rather than the ones that happen to break.
 *
 * `register()` returns a fresh `ref` callback on every render, and react-hook-form
 * depends on it: React detaches the old one and attaches the new, which is when
 * the library writes a value back into an uncontrolled input. The compiler
 * memoises the spread, the callback identity stops changing, React stops
 * calling it — and `reset(values)` silently leaves the DOM as the reader typed
 * it. Measured: discard restored nothing, and the next edit no longer raised
 * the unsaved-changes guard.
 *
 * The compiler already declines several of these on its own — that is the
 * `react-hooks/incompatible-library` warning — but which ones it declines is
 * not something to depend on.
 */
'use no memo'

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { isAppError } from '@/shared/api'
import { SESSION_QUERY_KEY } from '@/shared/config'
import { useErrorMessage } from '@/shared/lib/use-error-message'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FloatingField,
} from '@/shared/ui'

import { authSettingsQuery, minPasswordLengthOf } from '../api/settings-api'
import { changePassword } from '../model/session-lifecycle'

const schemaFor = (minPasswordLength: number) =>
  z
    .object({
      currentPassword: z.string().min(1, { message: 'common:validation.required' }),
      newPassword: z.string().min(minPasswordLength),
      repeatPassword: z.string(),
    })
    .refine((values) => values.newPassword === values.repeatPassword, {
      path: ['repeatPassword'],
      message: 'mismatch',
    })

type PasswordValues = { currentPassword: string; newPassword: string; repeatPassword: string }

export function ChangePasswordCard() {
  const { t } = useTranslation(['auth', 'common'])
  const queryClient = useQueryClient()
  const errorMessage = useErrorMessage()
  const { data: settings } = useQuery(authSettingsQuery())
  const minPasswordLength = minPasswordLengthOf(settings)

  const change = useMutation({
    mutationFn: (values: PasswordValues) =>
      changePassword(values.currentPassword, values.newPassword),
    onSuccess: async () => {
      // The flag lives in the credential store and has just been cleared, so
      // the guard has to re-read the session before it stops redirecting.
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: ['auth-sessions'] })
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({
    resolver: zodResolver(schemaFor(minPasswordLength)),
    defaultValues: { currentPassword: '', newPassword: '', repeatPassword: '' },
    // Validating on blur paints a field red the moment focus leaves it, so
    // tabbing through the form is enough to light it up before anything has
    // been typed. Complain on submit, then correct live as the user fixes it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await change.mutateAsync(values)
      reset()
    } catch (error) {
      // Two fields, and the provider says outright which one it rejected — so
      // the message goes on the field rather than into a form-level alert.
      if (isAppError(error) && error.code === 'GOAUTH_INVALID_CREDENTIALS') {
        setError('currentPassword', { message: t('account.password.wrongCurrent') })
        return
      }
      if (isAppError(error) && error.code === 'GOAUTH_PASSWORD_TOO_SHORT') {
        setError('newPassword', { message: t('common:validation.min', { count: minPasswordLength }) })
        return
      }

      setError('root', {
        message: isAppError(error) ? errorMessage(error) : t('common:errors.UNKNOWN'),
      })
    }
  })

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
      <Card>
        <CardHeader>
          <CardTitle>{t('account.password.title')}</CardTitle>
          <CardDescription>{t('account.password.description')}</CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {errors.root !== undefined && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <FloatingField
            label={t('account.password.current')}
            type="password"
            autoComplete="current-password"
            className="w-field-md"
            tone={errors.currentPassword === undefined ? 'normal' : 'error'}
            // The schema puts a key here and `setError` puts a finished
            // sentence; `t` passes the latter through untouched.
            hint={
              errors.currentPassword === undefined
                ? undefined
                : t(errors.currentPassword.message ?? 'common:validation.required')
            }
            {...register('currentPassword')}
          />

          <FloatingField
            label={t('account.password.next')}
            type="password"
            autoComplete="new-password"
            className="w-field-md"
            tone={errors.newPassword === undefined ? 'normal' : 'error'}
            // The rule is the same sentence whether it is advice or a complaint.
            hint={t('common:validation.min', { count: minPasswordLength })}
            {...register('newPassword')}
          />

          <FloatingField
            label={t('account.password.repeat')}
            type="password"
            autoComplete="new-password"
            className="w-field-md"
            tone={errors.repeatPassword === undefined ? 'normal' : 'error'}
            hint={errors.repeatPassword === undefined ? undefined : t('account.password.mismatch')}
            {...register('repeatPassword')}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" variant="default" isLoading={isSubmitting}>
          {t('account.password.submit')}
        </Button>
      </div>
    </form>
  )
}
